import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById } from '@/mocks/articles';
import { comics } from '@/mocks/comics';
import { categoryNames } from '@/mocks/categories';
import ComicCard from '@/components/feature/ComicCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl text-foreground-300" />
            <h1 className="mt-4 text-xl font-semibold text-foreground-950">文章未找到</h1>
            <p className="mt-2 text-foreground-500">该漫画文章不存在或已被删除</p>
            <Link
              to="/"
              className="mt-6 inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categoryNames[article.category] || article.category;
  const relatedComics = comics
    .filter((c) => c.category === article.category && c.id !== article.id)
    .slice(0, 4);

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 韩国漫画推荐与评测`}
        description={article.summary}
        keywords={`${article.title},韩国漫画,${article.category}漫画,韩漫推荐,韩漫漫画网,${article.tags.join(',')}`}
        canonicalUrl={`https://www.hanmancomic.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        publishedTime={article.publishDate}
        modifiedTime={article.updateDate}
        author={article.author}
      />
      <SchemaOrg
        pageType="Article"
        breadcrumbs={[
          { name: '首页', url: '/' },
          { name: catName, url: `/category/${article.category}` },
          { name: article.title, url: `/article/${article.id}` },
        ]}
        articleData={{
          headline: article.title,
          description: article.summary,
          author: article.author,
          datePublished: article.publishDate,
          dateModified: article.updateDate,
          image: article.coverImage,
        }}
        faqData={article.faqs}
      />

      <Navbar />

      <main className="min-h-screen">
        <article className="px-4 md:px-6 py-6 md:py-8 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: catName, url: `/category/${article.category}` },
              { name: article.title, url: `/article/${article.id}` },
            ]}
          />

          <header className="mb-6 md:mb-8">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="hidden sm:inline text-foreground-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishDate}
              </span>
              <span className="hidden sm:inline text-foreground-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
              <span className="hidden sm:inline text-foreground-300">|</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${article.status === '已完结' ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-700'}`}>
                {article.status}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-1.5">
              <span className="flex items-center gap-0.5 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`text-sm ${i < Math.floor(article.rating / 2) ? 'ri-star-fill' : i < article.rating / 2 ? 'ri-star-half-fill' : 'ri-star-line'}`}
                  />
                ))}
              </span>
              <span className="text-sm font-semibold text-foreground-800 ml-1">{article.rating}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="mb-6 md:mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={`${article.title} - 韩国漫画封面`}
              title={`${article.title} - 韩漫漫画网推荐`}
              className="w-full max-h-[400px] md:max-h-[500px] object-cover object-top"
            />
          </div>

          <div className="prose-custom text-foreground-800 leading-relaxed">
            <p className="text-base md:text-lg text-foreground-700 mb-6 md:mb-8 p-4 md:p-5 bg-background-100 rounded-lg border-l-4 border-primary-500">
              <strong>摘要：</strong>{article.summary}
            </p>

            {article.sections.map((section, index) => (
              <section key={index} className="mb-6 md:mb-8">
                <h2 className="font-heading text-lg md:text-2xl font-semibold text-foreground-950 mb-3 md:mb-4">
                  {section.heading}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </section>
            ))}
          </div>

          {article.faqs.length > 0 && (
            <section className="mt-10 md:mt-14 pt-8 md:pt-10 border-t border-background-200/70">
              <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-4 md:mb-6">
                常见问题
              </h2>
              <div className="space-y-3 md:space-y-4">
                {article.faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-900 list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg" />
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {relatedComics.length > 0 && (
            <section className="mt-10 md:mt-14 pt-8 md:pt-10 border-t border-background-200/70">
              <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-4 md:mb-6">
                相关漫画推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5">
                {relatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}